// src/pages/Education.tsx
import React, { useState } from 'react';
import './Education.css';
import { FaPlay, FaGraduationCap, FaCertificate, FaAward, FaBook, FaSchool, FaUniversity, FaFileAlt } from 'react-icons/fa';
import { SiCoursera, SiGoogle, SiUdemy } from 'react-icons/si';

// Define types for education data
interface EducationData {
  institution: string;
  degree: string;
  period: string;
  gpa: string;
  minor?: string;
  achievements?: string[];
  keyCourses?: string[];
  background: string;
  icon: JSX.Element;
}

// Updated education data with your requested changes
const EDUCATION_DATA: EducationData[] = [
  {
    institution: "Pennsylvania State University", // Updated full name
    degree: "Bachelor of Science in Computer Science and Mathematics",
    period: "2022 - December 2025",
    gpa: "3.6",
    minor: "Business Administration",
    achievements: [
      "Dean's List in 5 of 7 semesters",
      "Multiple Hackathon Winner",
      "Learning Assistant for 4 subjects across 3 disciplines",
    ],
    keyCourses: [
      "Data Structures & Algorithms",
      "Object-Oriented Programming",
      "Software Design",
      "System Programming",
      "Computer Networks",
      "Database Management",
      "Cybersecurity Fundamentals",
      "Discrete Mathematics", 
      "Advanced Algorithms",
      "Digital Design",
      "Statistics",
      "Linear Programming"
    ],
    background: "#1a365d",
    icon: <FaUniversity />
  },
  {
    institution: "St. Xaviers School",
    degree: "High School Diploma in Science and Mathematics",
    period: "2018 - 2022",
    gpa: "3.7",
    background: "#4a235a",
    icon: <FaSchool />
  }
];

// Certification icon mapping with available icons
const certificationIcons: { [key: string]: JSX.Element } = {
  'harvard': <FaUniversity />,
  'coursera': <SiCoursera />,
  'google': <SiGoogle />,
  'stanford': <FaUniversity />,
  'udemy': <SiUdemy />,
  'university': <FaUniversity />,
  'default': <FaCertificate />
};

const Education: React.FC = () => {
  const [viewMode, setViewMode] = useState<'all' | 'education' | 'certifications'>('all');
  const [expanded, setExpanded] = useState(false);

  // Fallback certifications
  const fallbackCertifications = [
    {
      title: "CS50 by Harvard University",
      issuer: "Harvard University",
      issuedDate: "2023",
      link: "https://example.com/certification1",
      iconName: "harvard"
    },
    {
      title: "Advanced Python Specialization",
      issuer: "Coursera",
      issuedDate: "2023",
      link: "https://example.com/certification2",
      iconName: "coursera"
    },
    {
      title: "Google Data Analytics Professional Certificate",
      issuer: "Google",
      issuedDate: "2024",
      link: "https://example.com/certification3",
      iconName: "google"
    },
    {
      title: "Machine Learning Specialization",
      issuer: "Stanford Online",
      issuedDate: "2024",
      link: "https://example.com/certification4",
      iconName: "stanford"
    }
  ];

  const displayedCertifications = fallbackCertifications;

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const getIcon = (iconName: string) => {
    return certificationIcons[iconName] || certificationIcons.default;
  };

  return (
    <div className="netflix-education-container">
      {/* Banner section */}
      <div 
        className="education-banner"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="banner-content">
          <h1 className="netflix-title">ACADEMIC JOURNEY</h1>
          
          <div className="netflix-meta">
            <span className="match">98% Match</span>
            <span className="year">2025</span>
            <span className="rating">EDU</span>
            <span className="duration">Graduation: December 2025</span>
          </div>

          <p className="netflix-description">
            Explore my educational background and professional certifications.
            Completed a Bachelor's in Computer Science and Mathematics at
            Pennsylvania State University with a minor in Business Administration (December 2025).
          </p>

          <div className="netflix-buttons">
            <button 
              className={`btn-play ${viewMode === 'all' ? 'active' : ''}`} 
              onClick={() => setViewMode('all')}
            >
              <FaPlay />
              <span>All</span>
            </button>
            <button 
              className={`btn-category ${viewMode === 'education' ? 'active' : ''}`} 
              onClick={() => setViewMode('education')}
            >
              <FaGraduationCap />
              <span>Education</span>
            </button>
            <button 
              className={`btn-category ${viewMode === 'certifications' ? 'active' : ''}`} 
              onClick={() => setViewMode('certifications')}
            >
              <FaCertificate />
              <span>Certifications</span>
            </button>
          </div>
        </div>
      </div>

      {/* Formal Education Heading - positioned below banner */}
      <div className="formal-education-heading">
        <h2>Formal Education</h2>
      </div>

      {/* Education Section */}
      {(viewMode === 'all' || viewMode === 'education') && (
        <section className="education-section">
          <div className={`education-grid ${expanded ? 'expanded-view' : ''}`}>
            {/* Penn State Card */}
            <div 
              className={`education-card college-card ${expanded ? 'expanded' : ''}`}
              style={{ background: EDUCATION_DATA[0].background }}
            >
              <div className="education-header">
                <div className="education-icon">{EDUCATION_DATA[0].icon}</div>
                <div className="education-title-section">
                  <h3 className="education-title">{EDUCATION_DATA[0].institution}</h3>
                  <p className="education-degree">{EDUCATION_DATA[0].degree}</p>
                  <p className="education-period">{EDUCATION_DATA[0].period}</p>
                </div>
              </div>
              
              <div className="education-details">
                <div className="education-stats">
                  <div className="stat gpa-stat">
                    <span className="stat-label">GPA</span>
                    <span className="stat-value">{EDUCATION_DATA[0].gpa}</span>
                  </div>
                  {EDUCATION_DATA[0].minor && (
                    <div className="stat minor-stat">
                      <span className="stat-label">MINOR</span>
                      <span className="stat-value">{EDUCATION_DATA[0].minor}</span>
                    </div>
                  )}
                </div>
                
                {expanded && (
                  <div className="education-expanded">
                    {EDUCATION_DATA[0].achievements && (
                      <div className="achievements">
                        <h4><FaAward /> Achievements</h4>
                        <ul>
                          {EDUCATION_DATA[0].achievements.map((achievement, i) => (
                            <li key={i}>{achievement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {EDUCATION_DATA[0].keyCourses && (
                      <div className="key-courses">
                        <h4><FaBook /> Key Courses</h4>
                        <div className="courses-grid">
                          {EDUCATION_DATA[0].keyCourses.map((course, i) => (
                            <span key={i} className="course-tag">{course}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="card-footer">
                <button className="toggle-btn" onClick={toggleExpand}>
                  {expanded ? 'Show Less' : 'Show More'}
                </button>
                <button 
                  type="button"
                  className="transcript-btn"
                  onClick={() => alert("Transcript would open in a new window")}
                >
                  <FaFileAlt /> View Transcript
                </button>
              </div>
            </div>

            {/* High School Card */}
            <div 
              className="education-card high-school-card"
              style={{ background: EDUCATION_DATA[1].background }}
            >
              <div className="education-header">
                <div className="education-icon">{EDUCATION_DATA[1].icon}</div>
                <div className="education-title-section">
                  <h3 className="education-title">{EDUCATION_DATA[1].institution}</h3>
                  <p className="education-degree">{EDUCATION_DATA[1].degree}</p>
                  <p className="education-period">{EDUCATION_DATA[1].period}</p>
                </div>
              </div>
              
              <div className="education-details">
                <div className="education-stats">
                  <div className="stat gpa-stat">
                    <span className="stat-label">GPA</span>
                    <span className="stat-value">{EDUCATION_DATA[1].gpa}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Certifications Section */}
      {(viewMode === 'all' || viewMode === 'certifications') && (
        <section className="certifications-section">
          <h2 className="netflix-category-title">Professional Certifications</h2>
          <div className="certifications-grid">
            {displayedCertifications.map((cert, index) => (
              <a 
                href={cert.link} 
                key={index} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="certification-card"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="certification-icon">{getIcon(cert.iconName)}</div>
                <div className="certification-content">
                  <h3 className="certification-title">{cert.title}</h3>
                  <p className="certification-issuer">{cert.issuer}</p>
                  {cert.issuedDate && <span className="certification-date">{cert.issuedDate}</span>}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Education;